import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { inr, fmtDate } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { OrderInputs, BaseRows, Row, multiInvalid, PenaltyRows, RainRows, rainInvalid } from "@/components/OrderBreakdownRows";

const REASONS = ["Correction of wrong entry", "Supervisor update", "Missed entry update", "Data reconciliation", "Other"];

export function EditDailyOrderModal({ record, onClose, onSaved }) {
  const { role } = useApp();
  const [form, setForm] = useState(null);
  const [reason, setReason] = useState(REASONS[0]);
  const [customReason, setCustomReason] = useState("");
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!record) return;
    const b = record.breakdown || {};
    setForm({
      orders: String(record.orders ?? ""),
      multi_orders: String(record.multi_orders ?? 0),
      denial_orders: String(record.denial_orders ?? 0), cancelled_orders: String(record.cancelled_orders ?? 0),
      total_distance: String(record.total_distance ?? ""),
      extra_distance: String(b.extra_distance_km ?? ""),
      rain_orders: String(b.rain_orders ?? 0),
      other_incentive: String(b.other_incentive ?? ""),
      other_adjustment: String(b.other_adjustment ?? ""),
      remarks: record.remarks ?? "",
    });
    setReason(REASONS[0]);
    setCustomReason("");
  }, [record]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doPreview = useCallback(async (f) => {
    if (!record) return;
    try {
      const { data } = await api.post("/daily/preview", {
        dp_id: record.dp_id, date: record.date,
        orders: Number(f.orders || 0), multi_orders: Number(f.multi_orders || 0), denial_orders: Number(f.denial_orders || 0), cancelled_orders: Number(f.cancelled_orders || 0), rain_orders: Number(f.rain_orders || 0),
        extra_distance: Number(f.extra_distance || 0),
        other_incentive: Number(f.other_incentive || 0),
        other_adjustment: Number(f.other_adjustment || 0),
      });
      setPreview(data);
    } catch (e) { /* noop */ }
  }, [record]);

  useEffect(() => {
    if (!record || !form) return;
    const t = setTimeout(() => doPreview(form), 180);
    return () => clearTimeout(t);
  }, [form, record, doPreview]);

  const save = async () => {
    setSaving(true);
    try {
      const finalReason = reason === "Other" ? (customReason.trim() || "Other") : reason;
      await api.post("/daily", {
        dp_id: record.dp_id, date: record.date, orders: Number(form.orders || 0),
        multi_orders: Number(form.multi_orders || 0),
        denial_orders: Number(form.denial_orders || 0), cancelled_orders: Number(form.cancelled_orders || 0),
        total_distance: Number(form.total_distance || 0),
        extra_distance: Number(form.extra_distance || 0), rain_orders: Number(form.rain_orders || 0),
        other_incentive: Number(form.other_incentive || 0),
        other_adjustment: Number(form.other_adjustment || 0),
        remarks: form.remarks, force_edit: true, _user: role, _reason: finalReason,
      });
      toast.success(`Record updated for ${record.dp_name}`, {
        description: `${fmtDate(record.date)} · ${form.orders} orders · ${inr(preview?.total_earnings)}`,
      });
      window.dispatchEvent(new CustomEvent("ct-refresh"));
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update record");
    } finally { setSaving(false); }
  };

  const slabReach = Number(form?.orders || 0) >= 45 ? "₹70/order slab achieved" :
    Number(form?.orders || 0) >= 26 ? "₹65/order slab achieved" :
    Number(form?.orders || 0) >= 1 ? "₹60/order slab" : "";

  return (
    <Dialog open={!!record} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-card sm:max-w-lg" data-testid="edit-daily-modal">
        {record && form && (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-2">
                <Pencil className="h-5 w-5 text-primary" /> Edit Daily Record
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                {record.dp_name} · <span className="font-mono">{record.fe_id}</span> · {fmtDate(record.date)} <span className="text-xs">(date locked)</span>
              </p>
            </DialogHeader>

            <div className="mt-2 flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/5 px-4 py-2.5 text-xs" data-testid="edit-daily-before">
              <span className="font-medium text-amber-500">Previous</span>
              <span className="tabular font-semibold">
                {record.orders} orders ({record.multi_orders || 0} multi) · {inr(record.total_earnings)}
                {record.franchise_earning != null && <span className="ml-2 text-violet-500">Fr: {inr(record.franchise_earning)}</span>}
              </span>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <OrderInputs form={form} set={set} prefix="edit-daily" preview={preview} />
              {(record.breakdown?.rain_legacy_flat > 0 || (!("rain_orders" in (record.breakdown || {})) && record.breakdown?.rain_charges > 0)) &&
                <p className="col-span-2 -mt-1 text-[11px] text-amber-500" data-testid="edit-daily-rain-legacy-note">This entry has an old flat rain charge of {inr(record.breakdown.rain_charges)}. It is kept unless you enter rain orders, which replace it.</p>}
              {slabReach && <p className="col-span-2 -mt-2 flex items-center gap-1 text-xs font-medium text-emerald-500"><TrendingUp className="h-3 w-3" />{slabReach} (by total orders)</p>}
              <div><Label>Total Distance (KM)</Label><Input type="number" value={form.total_distance} onChange={(e) => set("total_distance", e.target.value)} /></div>
              <div><Label>Extra Distance (KM)</Label><Input data-testid="edit-daily-extra-distance" type="number" value={form.extra_distance} onChange={(e) => set("extra_distance", e.target.value)} /></div>
              <div><Label>Other Incentive (₹)</Label><Input type="number" value={form.other_incentive} onChange={(e) => set("other_incentive", e.target.value)} /></div>
              <div><Label>Other Adjustment (₹)</Label><Input type="number" value={form.other_adjustment} onChange={(e) => set("other_adjustment", e.target.value)} /></div>
              <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e) => set("remarks", e.target.value)} /></div>
              <div className="col-span-2">
                <Label>Edit Reason <span className="text-rose-500">*</span></Label>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger data-testid="edit-daily-reason-select" className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{REASONS.map((r) => <SelectItem key={r} value={r} data-testid={`edit-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{r}</SelectItem>)}</SelectContent>
                </Select>
                {reason === "Other" && (
                  <Input data-testid="edit-daily-custom-reason" className="mt-2" placeholder="Specify reason…"
                    value={customReason} onChange={(e) => setCustomReason(e.target.value)} />
                )}
              </div>
            </div>

            {preview && (
              <div className="mt-5 rounded-xl border border-primary/30 bg-primary/5 p-4" data-testid="edit-daily-breakdown">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">New Earning Breakdown · Slab {preview.slab_selected}</p>
                <div className="mt-3 space-y-1.5 text-sm">
                  <BaseRows p={preview} />
                  <RainRows p={preview} />
                  <Row l={`Extra Distance (${preview.extra_distance_km} × ₹${preview.extra_distance_rate})`} v={inr(preview.extra_distance_payout)} />
                  <Row l="Other Incentive" v={inr(preview.other_incentive)} />
                  <Row l="Other Adjustment" v={inr(preview.other_adjustment)} />
                  <PenaltyRows p={preview} />
                </div>
                <div className="mt-3 flex items-center justify-between border-t border-primary/20 pt-3">
                  <span className="font-head text-sm font-bold uppercase tracking-wide">New Total Earnings</span>
                  <span data-testid="edit-daily-total" className="font-head text-2xl font-bold tabular text-emerald-500">{inr(preview.total_earnings)}</span>
                </div>
                {preview.franchise_earning != null && (
                  <div className="mt-3 flex items-center justify-between rounded-lg bg-violet-500/10 px-3 py-2" data-testid="edit-daily-franchise">
                    <span className="text-xs text-violet-500">Franchise ({preview.franchise_referral}) · {preview.order_count} × ₹{preview.franchise_rate}</span>
                    <span className="tabular text-sm font-bold text-violet-500">{inr(preview.franchise_earning)}</span>
                  </div>
                )}
              </div>
            )}

            <Button data-testid="edit-daily-save-btn" onClick={save}
              disabled={saving || form.orders === "" || multiInvalid(form) || rainInvalid(form) || (reason === "Other" && !customReason.trim())}
              className="mt-5 h-12 w-full text-base font-semibold active:scale-[0.98]">
              {saving ? "Saving…" : "Save Changes"}
            </Button>
            <p className="mt-2 text-center text-[11px] text-muted-foreground">
              This edit is recorded in the audit trail with your name, reason, and before/after values.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
